import { httpUserTransactions } from "api/support/user";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import SupportSidebar from "components/layout/SupportSidebar";
import Filter from "components/ui/Filter";
import Pagination from "components/ui/Pagination";
import Search from "components/ui/Search";
import Table from "components/ui/Table";
import Toggle from "components/ui/Toggle";
import { Itransaction } from "components/ui/Transaction";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import currency, { cryptoFormat, moneyFormat } from "utilities/currency";
import { capitalize } from "utilities/misc";
import TransactionInfo from "../transactions/TransactionInfo";

const typesTitle: any = {
  Fiat: "naira",
  "Buy & Sell": "market",
  "Send & Receive": "send-receive",
  Swap: "swap",
  "Recurring Buy": "recurring",
};

const limit = 50;
const UserTransactions: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [transaction, setTransaction] = useState<Itransaction | null>(null);
  const [value, setValue] = useState("Fiat");
  const [filter, setFilter] = useState("deposit");
  const params = useParams();
  const userID = params.user as string;

  const typeValue = typesTitle[value];

  const placeNFilter = () => {
    if (typeValue === "naira") {
      return {
        filter,
        place: "",
      };
    } else if (typeValue === "market" || typeValue === "send-receive") {
      let sp = filter.split("-");
      return {
        filter: sp[0],
        place: sp[1],
      };
    } else if (typeValue === "swap") {
      return {
        filter: "swap",
        place: "",
      };
    }

    return {
      filter,
      place: "",
    };
  };

  useEffect(() => {
    setCurrentPage(1);
    setTransaction(null);
    setIsOpen(false);
  }, [userID]);

  const currencyType = typeValue === "naira" ? "naira" : "crypto";

  const {
    data: { transactions, total, page },
    isFetching,
    isError,
    refetch,
  } = useQuery(
    [
      "userTransactions",
      userID,
      currencyType,
      placeNFilter().filter,
      placeNFilter().place,
      currentPage,
    ],
    () =>
      httpUserTransactions(
        userID,
        currencyType,
        placeNFilter().filter,
        placeNFilter().place,
        limit,
        currentPage,
      ),
    {
      initialData: {
        transactions: [],
      },
    },
  );

  const handleClick = (tr: Itransaction) => {
    if (transaction?.id === tr.id) {
      setTransaction(null);
      setIsOpen(false);
    } else {
      setTransaction(tr);
      setIsOpen(true);
    }
  };

  const filters = (typeValue = typesTitle[value]) => {
    if (typeValue === "naira") {
      return [
        {
          label: "Deposit Fiat",
          value: "deposit",
        },
        {
          label: "Send Money",
          value: "withdrawal",
        },
        {
          label: "Earned Fiat",
          value: "earning",
        },
        {
          label: "Refund",
          value: "refund",
        },
        {
          label: "Pay Bills",
          value: "bill",
        },
      ];
    } else if (typeValue === "market") {
      return [
        {
          label: "Buy to external wallet",
          value: "buy-external",
        },
        {
          label: "Buy to Bitoshi wallet",
          value: "buy-bitoshi",
        },
        {
          label: "Sell from external wallet",
          value: "sell-external",
        },
        {
          label: "Sell from Bitoshi wallet",
          value: "sell-bitoshi",
        },
      ];
    } else if (typeValue === "send-receive") {
      return [
        {
          label: "Send: External transfer",
          value: "send-external",
        },
        {
          label: "Send: Internal transfer",
          value: "send-bitoshi",
        },
        {
          label: "Receive: External transfer",
          value: "receive-external",
        },
        {
          label: "Receive: Internal transfer",
          value: "receive-bitoshi",
        },
        {
          label: "Send Money",
          value: "withdrawal",
        },
        {
          label: "Refund",
          value: "refund",
        },
        {
          label: "Pay Bills",
          value: "bill",
        },
      ];
    } else if (typeValue === "recurring") {
      return [
        {
          label: "Active plans",
          value: "active",
        },
        {
          label: "Paused plans",
          value: "paused",
        },
        {
          label: "Cancelled plans",
          value: "stopped",
        },
        {
          label: "Purchases",
          value: "purchases",
        },
      ];
    }

    return [
      {
        label: "Swap",
        value: "swap",
      },
    ];
  };

  const transactionAmount = (tr: Itransaction) =>
    tr?.currencyType === "naira" ||
    ["bill", "withdrawal", "refund"].includes(tr?.transactionType)
      ? `${currency(tr.currency || "Fiat")} ${moneyFormat(tr?.amount)}`
      : `${cryptoFormat(tr?.amount || 0)} ${tr?.crypto?.toUpperCase() || ""}`;

  const firstTitle = () => {
    if (typeValue === "send-receive" || typeValue === "swap") {
      return "Type";
    } else {
      return "User";
    }
  };

  const firstItem = (transaction: Itransaction) => {
    if (placeNFilter().filter === "send") {
      return `Sent ${transaction.crypto?.toUpperCase()}`;
    } else if (placeNFilter().filter === "receive") {
      return `Received ${transaction.crypto?.toUpperCase()}`;
    } else if (placeNFilter().filter === "swap") {
      return `${transaction.fromCrypto?.toUpperCase()} - ${transaction.crypto?.toUpperCase()}`;
    }
    return `@${transaction?.user?.userName}`;
  };

  const secondTitle = () => {
    if (typeValue === "naira") {
      return capitalize(filter) + " amount";
    } else if (typeValue === "market") {
      return "Amount";
    } else if (typeValue === "send-receive" || typeValue === "swap") {
      return "User";
    } else {
      return "Asset";
    }
  };

  const secondItem = (transaction: Itransaction) => {
    if (typeValue === "send-receive" || typeValue === "swap") {
      return `@${transaction?.user?.userName}`;
    } else if (typeValue === "recurring") {
      return transaction.crypto?.toUpperCase();
    }
    return `${transactionAmount(transaction)}`;
  };

  const thirdTitle = () => {
    if (typeValue === "recurring" && filter !== "purchases") return "Frequency";
    else if (typeValue === "recurring" && filter === "purchases")
      return "Amount";
    return "Date & Time";
  };

  const thirdItem = (transaction: Itransaction) => {
    if (typeValue === "recurring") {
      if (filter === "purchases") {
        return `${currency(transaction.currency || "Fiat")} ${transaction.amount}`;
      }
      return capitalize(transaction.frequency);
    }
    return `${DateTime.fromISO(transaction.timestamp).toFormat(
      "DD",
    )} ${DateTime.fromISO(transaction.timestamp).toFormat("t")}`;
  };

  return (
    <Container>
      <SupportSidebar />
      <Main className="mx-5 mt-5 text-left">
        <div className="flex items-center justify-between w-full">
          <h3 className="text-2xl">@ transactions</h3>
          <div className="w-3/6">
            <Search type="user" />
          </div>
        </div>

        <div className="my-10">
          <Toggle
            data={[
              "Fiat",
              "Buy & Sell",
              "Send & Receive",
              "Swap",
              "Recurring Buy",
            ]}
            value={value}
            setValue={(nextValue) => {
              setValue(nextValue);
              setFilter(filters(typesTitle[nextValue])[0].value);
              setCurrentPage(1);
              setTransaction(null);
              setIsOpen(false);
            }}
          />

          {typeValue !== "swap" && (
            <div className="mt-10">
              <span className="text-sm">Filter by:</span>
              <Filter
                data={filters()}
                setValue={(nextValue) => {
                  setFilter(nextValue);
                  setCurrentPage(1);
                }}
                value={filter}
              />
            </div>
          )}
        </div>

        <div className="flex gap-8 mt-10">
          <div className="w-7/12">
            <Table
              isLoading={isFetching}
              loadingLabel="Loading transactions"
              emptyMessage="No transactions found"
              errorMessage={
                isError
                  ? "Unable to load transactions. Please try again."
                  : undefined
              }
              onRetry={() => {
                void refetch();
              }}
              header={[firstTitle(), secondTitle(), thirdTitle()]}
              data={[
                ...(transactions || []).map((transaction: Itransaction) => [
                  firstItem(transaction),
                  secondItem(transaction),
                  thirdItem(transaction),
                  () => handleClick(transaction),
                ]),
              ]}
            />
            <Pagination
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              limit={limit}
              page={page}
              total={total}
            />
          </div>

          {isOpen && <TransactionInfo transaction={transaction} />}
        </div>
      </Main>
    </Container>
  );
};
export default UserTransactions;
